/*
 * EcoHandel Batterij Calculator v3.0 (2026-04-19)
 * Shoppable capaciteit-adviseur voor thuisbatterijen.
 * Pure logic + DOM binding, geen dependencies.
 * v2.0: 1-fase/3-fase selector, complete set matching, /cart/add.js integratie.
 * v2.2: AJAX add-to-cart zonder redirect, quantity-stepper, stock-pill,
 *       analytics events, cart-count update in header.
 * v3.0: 11 set-configuraties uit losse SKU's (multi-product sets met quantities),
 *       componenten-lijst + totaalprijs per set, extras beperkt tot EV-charger.
 */
(function () {
  'use strict';

  /*
   * Berekening v3.1 (2026-05-08):
   *   - jaarverbruik en zonnepanelen zijn nu sliders (continue waarden)
   *   - zonnepanelen-correctie wordt lineair afgeleid van Wp
   *   - noodstroom-buffer schaalt nu MET het verbruik van het huishouden,
   *     niet meer een vast 0,5 kWh/uur. Bij 10.000 kWh/jaar geeft 24u
   *     noodstroom ~27 kWh extra (10000/8760 * 24 = 27,4).
   */

  // Zonnepanelen-correctie in kWh op basis van Wp.
  // 0 Wp -> 0; 4500 Wp -> ~4 kWh; 10000 Wp -> ~9 kWh (matcht oude enum-waarden).
  var ZON_KWH_PER_KWP = 0.9;
  function zonnepanelenCorrectie(wp) {
    var n = parseFloat(wp) || 0;
    if (n <= 0) return 0;
    return (n / 1000) * ZON_KWH_PER_KWP;
  }

  // Legacy enum mapping (voor optionalMatches + lead-tagging die op 'geen/klein/middel/groot/zeer-groot' werkt).
  function zonnepanelenLegacy(wp) {
    var n = parseFloat(wp) || 0;
    if (n <= 0) return 'geen';
    if (n < 3000) return 'klein';
    if (n < 6000) return 'middel';
    if (n < 10000) return 'groot';
    return 'zeer-groot';
  }

  // Noodstroom-buffer schaalt met jaarverbruik:
  //   buffer_kwh = (jaarverbruik / 8760) * uren
  // Voorbeelden bij 24 uur noodstroom:
  //   3.500 kWh/jaar -> 9,6 kWh extra
  //   6.000 kWh/jaar -> 16,4 kWh extra
  //   10.000 kWh/jaar -> 27,4 kWh extra
  function noodstroomBuffer(uren, jaarverbruik) {
    var u = parseFloat(uren) || 0;
    var jv = parseFloat(jaarverbruik) || 0;
    if (u <= 0 || jv <= 0) return 0;
    return (jv / 8760) * u;
  }

  function noodstroomLegacy(uren) {
    var n = parseFloat(uren) || 0;
    if (n <= 0) return 'geen';
    if (n < 8) return 'basis';
    return 'uitgebreid';
  }

  function ceil(n) { return Math.ceil(n); }

  // Legacy fallback voor zonnepanelen-correctie (oude enum: 'geen','klein',...)
  // Gebruikt als geen Wp-waarde is meegegeven (bv. door calculator-band homepage).
  var ZON_LEGACY_KWH = {
    'geen': 0,
    'klein': 2.0,
    'middel': 4.0,
    'groot': 6.5,
    'zeer-groot': 9.0
  };
  // Legacy fallback voor noodstroom (oude enum) — alleen relevant als geen uren-waarde gezet is.
  var NOODSTROOM_LEGACY_KWH = {
    'geen': 0,
    'basis': 5,
    'uitgebreid': 12
  };

  function berekenCapaciteit(inputs) {
    var verbruik = Math.max(0, Number(inputs.jaarverbruik_kwh) || 0);
    var dagverbruik = verbruik / 365;

    var contractFactor;
    switch (inputs.contract_type) {
      case 'dynamisch':              contractFactor = 0.55; break;
      case 'vast-zonder-saldering':  contractFactor = 0.75; break;
      case 'vast-met-saldering':     contractFactor = 0.40; break;
      default:                        contractFactor = 0.55;
    }
    var basis = dagverbruik * contractFactor;

    // Zon-correctie: sliders geven Wp door, calculator-band geeft legacy enum-tier door.
    var zonCorrectie;
    if (inputs.zonnepanelen_wp !== undefined && inputs.zonnepanelen_wp !== null && inputs.zonnepanelen_wp !== '') {
      zonCorrectie = zonnepanelenCorrectie(inputs.zonnepanelen_wp);
    } else if (inputs.zonnepanelen) {
      zonCorrectie = ZON_LEGACY_KWH[inputs.zonnepanelen] || 0;
    } else {
      zonCorrectie = 0;
    }
    var zonFactor = inputs.contract_type === 'vast-met-saldering' ? 0.4 : 1.0;
    basis += zonCorrectie * zonFactor;

    // EV en warmtepomp: max 5 kWh extra elk. Hoofdmoot komt uit verbruik,
    // zonnepanelen en noodvoorziening — deze 2 zijn comfort-upsells.
    var buffer = 0;
    if (inputs.warmtepomp) buffer += 5;
    if (inputs.ev_aanwezig) buffer += 5;

    // Noodstroom: slider geeft uren door (schaalt met verbruik), legacy enum geeft vaste kWh.
    var noodBuffer = 0;
    if (inputs.noodstroom_uren !== undefined && inputs.noodstroom_uren !== null && inputs.noodstroom_uren !== '') {
      noodBuffer = noodstroomBuffer(inputs.noodstroom_uren, verbruik);
    } else if (inputs.noodstroom) {
      noodBuffer = NOODSTROOM_LEGACY_KWH[inputs.noodstroom] || 0;
    }
    buffer += noodBuffer;

    var totaal = basis + buffer;

    return {
      minimaal: Math.max(5, ceil(totaal * 0.8)),
      aanbevolen: Math.max(5, ceil(totaal)),
      ruim: Math.max(10, ceil(totaal * 1.3)),
      // Debug-velden voor transparantie
      _basis: Math.round(basis * 10) / 10,
      _zonCorrectie: Math.round(zonCorrectie * 10) / 10,
      _noodBuffer: Math.round(noodBuffer * 10) / 10,
      _totaal: Math.round(totaal * 10) / 10
    };
  }

  function besparingPerJaar(aanbevolenKwh) {
    return Math.round(aanbevolenKwh * 0.15 * 300);
  }

  function pickTier(kwh, productsCatalog) {
    if (!productsCatalog || !Array.isArray(productsCatalog.tiers)) return null;
    for (var i = 0; i < productsCatalog.tiers.length; i++) {
      var tier = productsCatalog.tiers[i];
      if (kwh >= tier.min && kwh <= tier.max) return tier;
    }
    return productsCatalog.tiers[productsCatalog.tiers.length - 1];
  }

  function matchCompleteSet(kwh, fase, catalog) {
    var tier = pickTier(kwh, catalog);
    if (!tier || !tier.variants) return null;
    var variant = tier.variants[fase] || tier.variants.fase_1;
    if (!variant) return null;
    return {
      tier: tier,
      fase: fase,
      variant: variant
    };
  }

  function getInputs(form) {
    var verbruikInput = form.querySelector('[name="jaarverbruik_kwh"]');
    var verbruik = verbruikInput ? parseInt(verbruikInput.value, 10) || 0 : 0;

    var zonInput = form.querySelector('[name="zonnepanelen_wp"]');
    var zonLegacyInput = form.querySelector('[name="zonnepanelen"]');
    var wp = zonInput ? parseInt(zonInput.value, 10) || 0 : 0;

    var faseInput = form.querySelector('input[name="fase"]:checked');
    var noodSlider = form.querySelector('[name="noodstroom_uren"]');
    var noodLegacyInput = form.querySelector('[name="noodstroom"]');
    var uren = noodSlider ? parseFloat(noodSlider.value) || 0 : 0;

    // Contract: kan een select OF een radio-button-group zijn (calc-widget v3.2 gebruikt radios).
    var contractType;
    var contractCheckedRadio = form.querySelector('input[name="contract_type"]:checked');
    if (contractCheckedRadio) {
      contractType = contractCheckedRadio.value;
    } else {
      var contractEl = form.querySelector('select[name="contract_type"]');
      contractType = contractEl ? contractEl.value : 'dynamisch';
    }

    var warmtepompEl = form.querySelector('[name="warmtepomp"]');
    var evEl = form.querySelector('[name="ev_aanwezig"]');

    return {
      jaarverbruik_kwh: verbruik,
      zonnepanelen_wp: wp,
      // Legacy enum voor catalog-rules + optionals
      zonnepanelen: zonLegacyInput ? zonLegacyInput.value : zonnepanelenLegacy(wp),
      contract_type: contractType,
      warmtepomp: warmtepompEl ? warmtepompEl.checked : false,
      ev_aanwezig: evEl ? evEl.checked : false,
      noodstroom_uren: uren,
      noodstroom: noodLegacyInput ? noodLegacyInput.value : noodstroomLegacy(uren),
      fase: faseInput ? faseInput.value : 'fase_1'
    };
  }

  function formatEuro(n) {
    if (typeof n !== 'number') return '';
    return '€ ' + n.toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function pickHeroProduct(products) {
    if (!products || !products.length) return null;
    for (var i = 0; i < products.length; i++) {
      var role = (products[i].role || '').toLowerCase();
      if (role.indexOf('batter') !== -1) return products[i];
    }
    return products[0];
  }

  function sumTotal(products) {
    var total = 0;
    for (var i = 0; i < (products || []).length; i++) {
      var p = products[i];
      var q = p.quantity || 1;
      total += (p.price || 0) * q;
    }
    return total;
  }

  function renderComponentRow(p) {
    var qty = p.quantity || 1;
    var qtyBadge = qty > 1
      ? '<span class="calc-component__qty">' + qty + '&times;</span>'
      : '';
    var lineTotal = (p.price || 0) * qty;
    var priceStr = p.price
      ? '<span class="calc-component__price">' + formatEuro(lineTotal) + '</span>'
      : '';
    var image = p.image
      ? '<img src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy" width="64" height="64">'
      : '';
    var link = p.url
      ? '<a class="calc-component__link" href="' + escapeHtml(p.url) + '">' +
          'Bekijk product' +
          '<svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-arrow-right"></use></svg>' +
        '</a>'
      : '';
    return '<li class="calc-component">' +
      '<div class="calc-component__media">' + image + qtyBadge + '</div>' +
      '<div class="calc-component__body">' +
        '<span class="calc-component__role">' + escapeHtml(p.role || '') + '</span>' +
        '<span class="calc-component__title">' + escapeHtml(p.title || '') + '</span>' +
        (p.spec ? '<span class="calc-component__spec">' + escapeHtml(p.spec) + '</span>' : '') +
        link +
      '</div>' +
      priceStr +
    '</li>';
  }

  function buildItemsPayload(products) {
    var items = [];
    for (var i = 0; i < (products || []).length; i++) {
      var p = products[i];
      if (!p.variantId) continue;
      items.push({ id: String(p.variantId), quantity: p.quantity || 1 });
    }
    return items;
  }

  function renderSetCard(match, inputs) {
    var setVariant = match.variant;
    var products = setVariant.products || [];
    if (!products.length) return '';

    var total = typeof setVariant.total_price === 'number' ? setVariant.total_price : sumTotal(products);
    var capacityKwh = setVariant.capacity_kwh || null;
    var faseLabel = (match.fase === 'fase_3') ? '3-fase' : '1-fase';

    var upsellNote = setVariant.upsell
      ? '<div class="calc-set__upsell-note">' +
          '<svg class="calc-icon calc-icon--sm" aria-hidden="true" style="margin-right:0.4rem"><use href="#calc-ico-bolt"></use></svg>' +
          escapeHtml(setVariant.note || '') +
        '</div>'
      : '';
    var noteInline = (!setVariant.upsell && setVariant.note)
      ? '<p class="calc-set__note">' + escapeHtml(setVariant.note) + '</p>'
      : '';

    var itemsPayload = buildItemsPayload(products);
    var hasItems = itemsPayload.length > 0;
    var itemsAttr = hasItems
      ? ' data-items="' + escapeHtml(JSON.stringify(itemsPayload)) + '"'
      : '';

    var capacityBadge = capacityKwh
      ? '<span class="calc-set__capacity-badge">' + capacityKwh + ' kWh &middot; ' + faseLabel + '</span>'
      : '';

    var priceHtml = total
      ? '<div class="calc-set__price">' +
          '<span class="calc-set__price-label">Richtprijs complete set</span>' +
          '<span class="calc-set__price-amount">' + formatEuro(total) + '</span>' +
          '<span class="calc-set__price-sub">excl. btw &middot; excl. installatie</span>' +
        '</div>'
      : '';

    var stockPill = setVariant.upsell
      ? ''
      : '<span class="calc-stock calc-stock--in">' +
          '<svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-check-badge"></use></svg>' +
          'Op voorraad &middot; directe verzending' +
        '</span>';

    var qtyStepper = (hasItems && !setVariant.upsell)
      ? '<div class="calc-qty" data-qty>' +
          '<button type="button" class="calc-qty__btn" data-qty-dec aria-label="Aantal verlagen">' +
            '<svg class="calc-icon calc-icon--sm" aria-hidden="true"><use href="#calc-ico-minus"></use></svg>' +
          '</button>' +
          '<span class="calc-qty__value" data-qty-value aria-live="polite">1</span>' +
          '<button type="button" class="calc-qty__btn" data-qty-inc aria-label="Aantal verhogen">' +
            '<svg class="calc-icon calc-icon--sm" aria-hidden="true"><use href="#calc-ico-plus"></use></svg>' +
          '</button>' +
        '</div>'
      : '';

    var addButton = (hasItems && !setVariant.upsell)
      ? '<button type="button" class="calc-btn calc-btn--primary" data-add-set' + itemsAttr + '>' +
          '<svg class="calc-icon calc-icon--sm" aria-hidden="true" style="margin-right:0.6rem"><use href="#calc-ico-cart"></use></svg>' +
          'Voeg complete set toe' +
        '</button>'
      : '';

    var adviesBtn = setVariant.upsell
      ? '<a class="calc-btn calc-btn--primary" href="#calc-lead">' +
          '<svg class="calc-icon calc-icon--sm" aria-hidden="true" style="margin-right:0.6rem"><use href="#calc-ico-arrow-right"></use></svg>' +
          'Vraag persoonlijk advies aan' +
        '</a>'
      : '';

    var actionsRow = (qtyStepper || addButton || adviesBtn)
      ? '<div class="calc-set__actions">' + qtyStepper + addButton + adviesBtn + '</div>'
      : '';

    var componentRows = products.map(renderComponentRow).join('');
    var componentsBlock = componentRows
      ? '<div class="calc-set__components-inline">' +
          '<span class="calc-set__components-title">' +
            '<svg class="calc-icon calc-icon--sm" aria-hidden="true"><use href="#calc-ico-battery"></use></svg>' +
            products.length + (products.length === 1 ? ' onderdeel in deze set' : ' onderdelen in deze set') +
          '</span>' +
          '<ul class="calc-set__components-list">' + componentRows + '</ul>' +
        '</div>'
      : '';

    var html =
      upsellNote +
      '<article class="calc-set calc-set--v3' + (setVariant.upsell ? ' calc-set--muted' : '') + '">' +
        '<header class="calc-set__header">' +
          '<div class="calc-set__header-main">' +
            '<span class="calc-set__role">Jouw complete set</span>' +
            capacityBadge +
          '</div>' +
          priceHtml +
        '</header>' +
        componentsBlock +
        noteInline +
        (stockPill ? '<div class="calc-set__stock">' + stockPill + '</div>' : '') +
        actionsRow +
      '</article>';

    return html;
  }

  function optionalMatches(opt, inputs) {
    var rule = opt && opt.match;
    if (!rule) return false;
    if (rule === 'always') return true;
    if (rule === 'ev') return !!inputs.ev_aanwezig;
    if (rule === 'warmtepomp') return !!inputs.warmtepomp;
    if (rule === 'noodstroom') return inputs.noodstroom && inputs.noodstroom !== 'geen';
    return false;
  }

  function renderOptionals(inputs, catalog) {
    if (!catalog || !catalog.optionals) return '';
    var rows = [];
    for (var key in catalog.optionals) {
      if (!Object.prototype.hasOwnProperty.call(catalog.optionals, key)) continue;
      // Installatie wordt apart gerenderd via renderFeaturedInstallation() —
      // niet meedoen in de generieke optionals-grid om duplicatie te voorkomen.
      if (key === 'installatie') continue;
      var opt = catalog.optionals[key];
      if (opt && optionalMatches(opt, inputs)) {
        var reason = '';
        if (opt.match === 'ev') reason = 'Omdat je een elektrische auto hebt aangevinkt';
        if (opt.match === 'always') reason = 'Past bij jouw situatie';
        rows.push(renderOptional(opt, false, reason));
      }
    }
    if (!rows.length) return '';
    return '<div class="calc-optionals" data-optionals>' +
      '<div class="calc-optionals__header">' +
        '<span class="calc-optionals__eyebrow">' +
          '<svg class="calc-icon calc-icon--sm" aria-hidden="true"><use href="#calc-ico-sparkle"></use></svg>' +
          'Aanbevolen extra\u2019s' +
        '</span>' +
        '<h3 class="calc-optionals__title">Past bij jouw situatie</h3>' +
        '<p class="calc-optionals__subtitle">Op basis van je antwoorden passen deze componenten goed bij je setup.</p>' +
      '</div>' +
      '<div class="calc-optionals__grid">' + rows.join('') + '</div>' +
    '</div>';
  }

  function renderFeaturedInstallation(catalog) {
    if (!catalog || !catalog.optionals || !catalog.optionals.installatie) return '';
    var opt = catalog.optionals.installatie;
    var image = opt.image
      ? '<img src="' + escapeHtml(opt.image) + '" alt="' + escapeHtml(opt.title) + '" loading="lazy" width="120" height="120">'
      : '';
    var priceLine = opt.price
      ? '<span class="calc-install-hero__price"><span class="calc-install-hero__price-pre">Vanaf</span>' + formatEuro(opt.price) + '</span>'
      : '';
    var addBtn = opt.variantId
      ? '<button type="button" class="calc-btn calc-btn--primary calc-install-hero__cta" data-add-optional data-variant="' + escapeHtml(opt.variantId) + '" data-title="' + escapeHtml(opt.title) + '">' +
          '<svg class="calc-icon calc-icon--sm" aria-hidden="true" style="margin-right:0.5rem"><use href="#calc-ico-cart"></use></svg>' +
          'Voeg installatie toe' +
        '</button>'
      : '';
    return '<aside class="calc-install-hero" data-install-hero>' +
      '<div class="calc-install-hero__media">' + image +
        '<span class="calc-install-hero__badge">' +
          '<svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-check-badge"></use></svg>' +
          'Meest gekozen' +
        '</span>' +
      '</div>' +
      '<div class="calc-install-hero__body">' +
        '<span class="calc-install-hero__role">Aanbevolen extra</span>' +
        '<h3 class="calc-install-hero__title">' + escapeHtml(opt.title) + '</h3>' +
        '<ul class="calc-install-hero__bullets">' +
          '<li><svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-check-badge"></use></svg>Gecertificeerde monteur in heel Nederland</li>' +
          '<li><svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-check-badge"></use></svg>Plaatsing, aansluiting + back-up voorziening</li>' +
          '<li><svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-check-badge"></use></svg>Garantie op werk + 24-uurs RMA-lijn</li>' +
        '</ul>' +
        '<div class="calc-install-hero__action">' + priceLine + addBtn + '</div>' +
      '</div>' +
    '</aside>';
  }

  function renderOptional(opt, included, subtitle) {
    var image = opt.image
      ? '<img src="' + escapeHtml(opt.image) + '" alt="' + escapeHtml(opt.title) + '" loading="lazy" width="160" height="160">'
      : '';
    var iconId = opt.icon || 'sparkle';
    var rolePill = opt.role
      ? '<span class="calc-optional__pill">' +
          '<svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-' + escapeHtml(iconId) + '"></use></svg>' +
          escapeHtml(opt.role) +
        '</span>'
      : '';
    var reasonLine = subtitle
      ? '<p class="calc-optional__reason">' +
          '<svg class="calc-icon calc-icon--xs" aria-hidden="true"><use href="#calc-ico-sparkle"></use></svg>' +
          escapeHtml(subtitle) +
        '</p>'
      : '';
    var priceBlock = (!included && opt.price)
      ? '<div class="calc-optional__price-wrap">' +
          '<span class="calc-optional__price-label">Vanaf</span>' +
          '<span class="calc-optional__price">' + formatEuro(opt.price) + '</span>' +
        '</div>'
      : '';
    var action = included
      ? '<span class="calc-optional__included">Meegeleverd</span>'
      : '<button type="button" class="calc-btn calc-btn--primary calc-optional__cta" data-add-optional data-variant="' + escapeHtml(opt.variantId || '') + '" data-title="' + escapeHtml(opt.title) + '">' +
          '<svg class="calc-icon calc-icon--sm" aria-hidden="true" style="margin-right:0.5rem"><use href="#calc-ico-cart"></use></svg>' +
          'Voeg toe' +
        '</button>';
    return '<article class="calc-optional' + (included ? ' calc-optional--included' : '') + '">' +
      '<div class="calc-optional__media">' + image + '</div>' +
      '<div class="calc-optional__body">' +
        rolePill +
        '<h4 class="calc-optional__title">' + escapeHtml(opt.title) + '</h4>' +
        (opt.spec ? '<p class="calc-optional__spec">' + escapeHtml(opt.spec) + '</p>' : '') +
        reasonLine +
      '</div>' +
      '<div class="calc-optional__footer">' +
        priceBlock +
        action +
      '</div>' +
    '</article>';
  }

  function renderResult(widget, result, inputs, catalog) {
    var container = widget.querySelector('[data-calc-result]');
    if (!container) return;

    var mode = widget.getAttribute('data-mode') || 'full';
    var fullCalcUrl = widget.getAttribute('data-fullcalc-url') || '/pages/batterij-calculator';

    var match = matchCompleteSet(result.aanbevolen, inputs.fase, catalog);
    var setCapacity = (match && match.variant && match.variant.capacity_kwh) || result.aanbevolen;
    var displayKwh = setCapacity;
    var rangeMin = Math.min(result.minimaal, setCapacity);
    var rangeMax = Math.max(result.ruim, setCapacity);
    var faseLabel = inputs.fase === 'fase_3' ? '3-fase aansluiting' : '1-fase aansluiting';

    /* ---------- Compact-mode (homepage) ---------- */
    if (mode === 'compact') {
      var deepLink = fullCalcUrl + '?j=' + encodeURIComponent(inputs.jaarverbruik_kwh) +
        '&w=' + encodeURIComponent(inputs.zonnepanelen_wp) +
        '&u=' + encodeURIComponent(inputs.noodstroom_uren) +
        '&c=' + encodeURIComponent(inputs.contract_type) +
        '&f=' + encodeURIComponent(inputs.fase) +
        (inputs.warmtepomp ? '&wp=1' : '') +
        (inputs.ev_aanwezig ? '&ev=1' : '');

      var breakdownItems = [];
      breakdownItems.push('<li><span>Basis verbruik</span><strong>' + result._basis + ' kWh</strong></li>');
      if (result._noodBuffer > 0) {
        breakdownItems.push('<li><span>Noodvoorziening (' + inputs.noodstroom_uren + ' uur)</span><strong>+ ' + result._noodBuffer + ' kWh</strong></li>');
      }
      if (inputs.warmtepomp) breakdownItems.push('<li><span>Warmtepomp</span><strong>+ 5 kWh</strong></li>');
      if (inputs.ev_aanwezig) breakdownItems.push('<li><span>Elektrische auto</span><strong>+ 5 kWh</strong></li>');

      var compactHtml =
        '<div class="calc-result__headline calc-result__headline--compact">' +
          '<span class="calc-result__label">Aanbevolen capaciteit</span>' +
          '<span class="calc-result__kwh">' + result.aanbevolen + ' kWh</span>' +
          '<span class="calc-result__range">Bandbreedte: ' + result.minimaal + ' tot ' + result.ruim + ' kWh &middot; ' + faseLabel + '</span>' +
        '</div>' +
        (breakdownItems.length ? '<ul class="calc-result__breakdown">' + breakdownItems.join('') + '</ul>' : '') +
        '<div class="calc-result__cta-row calc-result__cta-row--compact">' +
          '<a class="calc-btn calc-btn--primary" href="' + deepLink + '">' +
            'Bekijk passende Deye-set' +
            '<svg class="calc-icon calc-icon--sm" aria-hidden="true" style="margin-left:0.6rem"><use href="#calc-ico-arrow-right"></use></svg>' +
          '</a>' +
          '<a class="calc-btn calc-btn--ghost" href="' + fullCalcUrl + '">Volledige calculator openen</a>' +
        '</div>';

      container.innerHTML = compactHtml;
      container.hidden = false;
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    /* ---------- Full mode (calculator-pagina) ---------- */
    var setHtml = match ? renderSetCard(match, inputs) : '<p class="calc-result__fallback">Voor deze configuratie helpen we je graag persoonlijk. <a href="/pages/contact">Neem contact op</a>.</p>';
    var installHeroHtml = renderFeaturedInstallation(catalog);
    var optionalsHtml = renderOptionals(inputs, catalog);

    var besparingBlock = '';
    if (inputs.contract_type === 'dynamisch') {
      var bespaard = besparingPerJaar(displayKwh);
      besparingBlock =
        '<div class="calc-result__besparing">' +
        '<span class="calc-result__besparing-label">Indicatieve jaarlijkse besparing</span>' +
        '<span class="calc-result__besparing-bedrag">' + formatEuro(bespaard) + '</span>' +
        '<span class="calc-result__besparing-sub">Bij een dynamisch contract en gemiddelde arbitrage.</span>' +
        '</div>';
    }

    var grootverbruikBlock = '';
    if (inputs.jaarverbruik_kwh > 25000) {
      grootverbruikBlock =
        '<div class="calc-result__zakelijk">' +
        '<p><strong>Dit verbruik valt in het zakelijke segment.</strong> Voor een systeem op deze schaal maken we graag een maatwerkadvies.</p>' +
        '<a class="calc-btn calc-btn--primary" href="/pages/installatie-partners">Neem contact op voor zakelijk advies</a>' +
        '</div>';
    }

    var html =
      '<div class="calc-result__headline">' +
        '<span class="calc-result__label">Aanbevolen capaciteit</span>' +
        '<span class="calc-result__kwh">' + displayKwh + ' kWh</span>' +
        '<span class="calc-result__range">Bandbreedte: ' + rangeMin + ' tot ' + rangeMax + ' kWh &middot; ' + faseLabel + '</span>' +
      '</div>' +
      besparingBlock +
      '<div class="calc-result__products">' +
        '<h4 class="calc-result__subtitle">Jouw complete Deye-set</h4>' +
        setHtml +
        installHeroHtml +
        optionalsHtml +
      '</div>' +
      grootverbruikBlock +
      '<div class="calc-result__cta-row">' +
        '<a class="calc-btn calc-btn--ghost" href="#calc-lead">Liever eerst persoonlijk advies?</a>' +
      '</div>';

    container.innerHTML = html;
    container.hidden = false;
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });

    var leadCapacity = widget.ownerDocument.querySelector('[data-lead-capacity]');
    if (leadCapacity) leadCapacity.value = displayKwh;
    var leadFeatures = widget.ownerDocument.querySelector('[data-lead-features]');
    if (leadFeatures) {
      var tags = [];
      if (inputs.warmtepomp) tags.push('warmtepomp');
      if (inputs.ev_aanwezig) tags.push('ev');
      if (inputs.noodstroom_uren > 0) tags.push('noodstroom-' + inputs.noodstroom_uren + 'uur');
      tags.push('contract-' + inputs.contract_type);
      tags.push('pv-' + inputs.zonnepanelen_wp + 'wp');
      tags.push(inputs.fase);
      leadFeatures.value = tags.join(',');
    }

    bindCartActions(container, inputs, match, catalog);
  }

  function validate(form) {
    var verbruikInput = form.querySelector('[name="jaarverbruik_kwh"]');
    if (!verbruikInput) return true;
    var val = parseInt(verbruikInput.value, 10);
    var errorEl = form.querySelector('[data-error="jaarverbruik_kwh"]');
    if (!val || val <= 0) {
      if (errorEl) errorEl.textContent = 'Kies een jaarverbruik van minstens 1.000 kWh.';
      verbruikInput.setAttribute('aria-invalid', 'true');
      return false;
    }
    if (errorEl) errorEl.textContent = '';
    verbruikInput.removeAttribute('aria-invalid');
    return true;
  }

  function loadCatalog(widget) {
    var url = widget.getAttribute('data-products-url');
    if (!url) return Promise.resolve(window.__EcoHandelCalcProducts || null);
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* ---------- Cart integration ---------- */

  function buildCartItems(variantIds) {
    return variantIds
      .filter(function (v) { return v; })
      .map(function (v) { return { id: parseInt(v, 10), quantity: 1 }; });
  }

  function addToCart(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw new Error(err.description || 'Toevoegen mislukt'); });
      return r.json();
    });
  }

  function showToast(message, linkText, linkUrl) {
    var existing = document.querySelector('.calc-toast');
    if (existing) existing.parentNode.removeChild(existing);

    var toast = document.createElement('div');
    toast.className = 'calc-toast';
    toast.setAttribute('role', 'status');

    var text = document.createElement('span');
    text.textContent = message;
    toast.appendChild(text);

    if (linkText && linkUrl) {
      var link = document.createElement('a');
      link.href = linkUrl;
      link.textContent = linkText;
      toast.appendChild(link);
    }
    document.body.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('is-visible'); });
    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, 400);
    }, 4200);
  }

  function trackCalcEvent(name, payload) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        window.dataLayer.push(Object.assign({ event: name }, payload || {}));
      }
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.lib && typeof window.ShopifyAnalytics.lib.track === 'function') {
        window.ShopifyAnalytics.lib.track(name, payload || {});
      }
    } catch (e) { /* noop */ }
  }

  function updateHeaderCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        var count = cart.item_count;
        var targets = document.querySelectorAll('[data-cart-count], .cart-count-bubble span[aria-hidden="true"], .js-cart-count, .cart-link__bubble-num');
        targets.forEach(function (t) { t.textContent = count; });
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { count: count, cart: cart } }));
      })
      .catch(function () { /* noop */ });
  }

  function bindQuantityStepper(container) {
    var steppers = container.querySelectorAll('[data-qty]');
    steppers.forEach(function (stepper) {
      var valueEl = stepper.querySelector('[data-qty-value]');
      var decBtn = stepper.querySelector('[data-qty-dec]');
      var incBtn = stepper.querySelector('[data-qty-inc]');
      if (!valueEl || !decBtn || !incBtn) return;
      var update = function (delta) {
        var current = parseInt(valueEl.textContent, 10) || 1;
        var next = Math.max(1, Math.min(99, current + delta));
        valueEl.textContent = next;
        decBtn.disabled = next <= 1;
      };
      decBtn.addEventListener('click', function () { update(-1); });
      incBtn.addEventListener('click', function () { update(1); });
      update(0);
    });
  }

  function bindCartActions(container, inputs, match, catalog) {
    bindQuantityStepper(container);

    var setBtn = container.querySelector('[data-add-set]');
    if (setBtn) {
      setBtn.addEventListener('click', function () {
        if (setBtn.disabled) return;
        var rawItems = setBtn.getAttribute('data-items') || '[]';
        var parsed = [];
        try { parsed = JSON.parse(rawItems) || []; } catch (e) { parsed = []; }
        var qtyValueEl = container.querySelector('[data-qty-value]');
        var mult = qtyValueEl ? (parseInt(qtyValueEl.textContent, 10) || 1) : 1;
        var items = parsed.map(function (p) {
          var id = parseInt(p.id, 10);
          var q = (p.quantity || 1) * mult;
          return { id: id, quantity: q };
        }).filter(function (i) { return i.id; });
        if (!items.length) return;
        setBtn.disabled = true;
        var original = setBtn.innerHTML;
        setBtn.textContent = 'Toevoegen...';
        addToCart(items).then(function () {
          setBtn.innerHTML = 'Toegevoegd &check;';
          updateHeaderCartCount();
          showToast('Complete set toegevoegd aan winkelmand.', 'Naar winkelmand', '/cart');
          trackCalcEvent('calculator_add_set', {
            kwh_aanbevolen: (match && match.tier ? match.tier.label : null),
            fase: inputs && inputs.fase,
            stepper_quantity: mult,
            variant_ids: items.map(function (i) { return i.id; })
          });
          setTimeout(function () {
            setBtn.innerHTML = original;
            setBtn.disabled = false;
          }, 3000);
        }).catch(function (err) {
          setBtn.disabled = false;
          setBtn.innerHTML = original;
          showToast('Er ging iets mis: ' + err.message);
        });
      });
    }

    var optionalBtns = container.querySelectorAll('[data-add-optional]');
    optionalBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant');
        var title = btn.getAttribute('data-title') || 'Product';
        if (!variantId) return;
        btn.disabled = true;
        var original = btn.textContent;
        btn.textContent = 'Toevoegen...';
        addToCart([{ id: parseInt(variantId, 10), quantity: 1 }]).then(function () {
          btn.textContent = 'Toegevoegd ✓';
          updateHeaderCartCount();
          showToast(title + ' toegevoegd aan winkelmand.', 'Naar winkelmand', '/cart');
          trackCalcEvent('calculator_add_optional', { variant_id: variantId, title: title });
          setTimeout(function () {
            btn.textContent = original;
            btn.disabled = false;
          }, 2500);
        }).catch(function (err) {
          btn.disabled = false;
          btn.textContent = original;
          showToast('Er ging iets mis: ' + err.message);
        });
      });
    });
  }

  /* ---------- Init ---------- */

  function initWidget(widget) {
    var form = widget.querySelector('[data-calc-form]');
    if (!form) return;

    var faseInputs = widget.querySelectorAll('[name="fase"]');
    var fase = 'fase_1';
    try {
      var storedFase = localStorage.getItem('ecohandel-calc-fase');
      if (storedFase === 'fase_1' || storedFase === 'fase_3') fase = storedFase;
    } catch (e) { /* noop */ }
    faseInputs.forEach(function (i) {
      i.checked = (i.value === fase);
      i.addEventListener('change', function () {
        if (i.checked) {
          fase = i.value;
          widget.setAttribute('data-fase', fase);
          try { localStorage.setItem('ecohandel-calc-fase', fase); } catch (e) {}
        }
      });
    });
    widget.setAttribute('data-fase', fase);

    // Slider helper: bind live-output + CSS track-fill var op een range input.
    function bindSlider(input, output, formatter) {
      if (!input) return null;
      var min = parseFloat(input.min);
      var max = parseFloat(input.max);
      if (isNaN(min)) min = 0;
      if (isNaN(max)) max = 100;
      var update = function () {
        var v = parseFloat(input.value);
        if (isNaN(v)) v = min;
        if (output) output.textContent = formatter(v);
        var pct = max > min ? ((v - min) / (max - min)) * 100 : 0;
        input.style.setProperty('--calc-slider-progress', pct + '%');
      };
      input.addEventListener('input', update);
      input.addEventListener('change', update);
      update();
      return update;
    }

    function nlNum(n) { return Math.round(n).toLocaleString('nl-NL'); }

    var jvSlider = form.querySelector('[name="jaarverbruik_kwh"]');
    var jvOutput = form.querySelector('[data-jaarverbruik-output]');
    var resetJv = bindSlider(jvSlider, jvOutput, function (v) { return nlNum(v) + ' kWh'; });

    var zonSlider = form.querySelector('[name="zonnepanelen_wp"]');
    var zonOutput = form.querySelector('[data-zonnepanelen-output]');
    var resetZon = bindSlider(zonSlider, zonOutput, function (v) {
      return v <= 0 ? 'geen' : nlNum(v) + ' Wp';
    });

    var noodSlider = form.querySelector('[name="noodstroom_uren"]');
    var noodOutput = form.querySelector('[data-noodstroom-output]');
    var resetNood = bindSlider(noodSlider, noodOutput, function (v) {
      return v <= 0 ? 'geen' : Math.round(v) + (Math.round(v) === 1 ? ' uur' : ' uur');
    });

    // URL-prefill (bv. uit homepage compact-mode deeplink: ?j=10000&w=4500&u=24&c=dynamisch&f=fase_1&wp=1&ev=1)
    try {
      var params = new URLSearchParams(window.location.search);
      var pre = {
        j: params.get('j'),
        w: params.get('w'),
        u: params.get('u'),
        c: params.get('c'),
        f: params.get('f'),
        wp: params.get('wp'),
        ev: params.get('ev')
      };
      if (jvSlider && pre.j !== null && !isNaN(parseInt(pre.j, 10))) jvSlider.value = pre.j;
      if (zonSlider && pre.w !== null && !isNaN(parseInt(pre.w, 10))) zonSlider.value = pre.w;
      if (noodSlider && pre.u !== null && !isNaN(parseInt(pre.u, 10))) noodSlider.value = pre.u;
      if (pre.c) {
        var contractRadio = form.querySelector('input[name="contract_type"][value="' + pre.c + '"]');
        if (contractRadio) {
          contractRadio.checked = true;
        } else {
          var contractSelEl = form.querySelector('select[name="contract_type"]');
          if (contractSelEl) contractSelEl.value = pre.c;
        }
      }
      if (pre.f === 'fase_1' || pre.f === 'fase_3') {
        var faseEl = form.querySelector('input[name="fase"][value="' + pre.f + '"]');
        if (faseEl) faseEl.checked = true;
      }
      var wpEl = form.querySelector('[name="warmtepomp"]');
      if (wpEl) wpEl.checked = pre.wp === '1';
      var evEl = form.querySelector('[name="ev_aanwezig"]');
      if (evEl) evEl.checked = pre.ev === '1';
      // Re-render slider outputs after prefill
      if (resetJv) resetJv();
      if (resetZon) resetZon();
      if (resetNood) resetNood();
      // Auto-submit als deeplink jaarverbruik bevat
      if (pre.j !== null) {
        window.requestAnimationFrame(function () {
          form.dispatchEvent(new Event('submit', { cancelable: true }));
        });
      }
    } catch (e) { /* no URLSearchParams: skip */ }

    form.addEventListener('reset', function () {
      window.requestAnimationFrame(function () {
        if (resetJv) resetJv();
        if (resetZon) resetZon();
        if (resetNood) resetNood();
      });
    });

    var catalogPromise = loadCatalog(widget);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!validate(form)) return;
      var inputs = getInputs(form);
      var result = berekenCapaciteit(inputs);
      catalogPromise.then(function (catalog) {
        renderResult(widget, result, inputs, catalog);
        trackCalcEvent('calculator_result', {
          aanbevolen_kwh: result.aanbevolen,
          range_min_kwh: result.minimaal,
          range_max_kwh: result.ruim,
          jaarverbruik_kwh: inputs.jaarverbruik_kwh,
          contract_type: inputs.contract_type,
          zonnepanelen: inputs.zonnepanelen,
          warmtepomp: inputs.warmtepomp,
          ev_aanwezig: inputs.ev_aanwezig,
          noodstroom_uren: inputs.noodstroom_uren,
          fase: inputs.fase
        });
      });
    });

    form.addEventListener('reset', function () {
      var container = widget.querySelector('[data-calc-result]');
      if (container) {
        container.innerHTML = '';
        container.hidden = true;
      }
    });
  }

  function bindHeroShopActions() {
    var buttons = document.querySelectorAll('[data-calc-hero-add]');
    buttons.forEach(function (btn) {
      if (btn.dataset.bound === '1') return;
      btn.dataset.bound = '1';
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        var variantId = parseInt(btn.getAttribute('data-variant'), 10);
        var title = btn.getAttribute('data-title') || 'Product';
        if (!variantId) return;
        var original = btn.innerHTML;
        btn.disabled = true;
        btn.textContent = 'Toevoegen...';
        addToCart([{ id: variantId, quantity: 1 }]).then(function () {
          btn.innerHTML = 'Toegevoegd &check;';
          updateHeaderCartCount();
          showToast(title + ' toegevoegd aan winkelmand.', 'Naar winkelmand', '/cart');
          trackCalcEvent('calculator_hero_add', { variant_id: variantId, title: title });
          setTimeout(function () {
            btn.innerHTML = original;
            btn.disabled = false;
          }, 3000);
        }).catch(function (err) {
          btn.disabled = false;
          btn.innerHTML = original;
          showToast('Er ging iets mis: ' + (err && err.message ? err.message : 'onbekende fout'));
        });
      });
    });
  }

  function onReady() {
    var widgets = document.querySelectorAll('[data-calc-widget]');
    widgets.forEach(initWidget);
    bindHeroShopActions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady);
  } else {
    onReady();
  }

  // Exports voor testing
  window.EcoHandelCalculator = {
    berekenCapaciteit: berekenCapaciteit,
    besparingPerJaar: besparingPerJaar,
    matchCompleteSet: matchCompleteSet
  };
})();
